
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Post, Follow, PostType, AppStats } from '../types';
import { THEME_COLORS } from '../App';
import ConfirmDialog from './ConfirmDialog';

interface AdminViewProps {
  posts: Post[];
  follows: Follow[];
  stats: AppStats;
  onPostCreate: (formData: FormData) => Promise<boolean>;
  onFollowAdd: (follow: { url: string; label: string }) => Promise<boolean>;
  onFollowUpdate: (follow: Follow) => void;
  onFollowDelete: (id: string) => void;
  onLogout: () => void;
  themeColor: string;
  onThemeColorChange: (color: string) => void;
}

const SHORT_POST_LIMIT = 200;
const LONG_POST_LIMIT = 2000;

const AdminView: React.FC<AdminViewProps> = ({ 
  posts, follows, stats, onPostCreate, onFollowAdd, onFollowUpdate, onFollowDelete, onLogout, 
  themeColor, onThemeColorChange 
}) => {
  const [activeTab, setActiveTab] = useState<'composer' | 'subscriptions' | 'system'>('composer');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostType, setNewPostType] = useState<PostType>(PostType.SHORT);
  const [isNsfw, setIsNsfw] = useState(false);
  
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);

  const [isPublic, setIsPublic] = useState(false);
  const [globalPollInterval, setGlobalPollInterval] = useState(60);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [isAddFeedOpen, setIsAddFeedOpen] = useState(false);
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newFeedUrlLabel, setNewFeedLabel] = useState('');
  const [isAddingFeed, setIsAddingFeed] = useState(false);
  const [hasCopiedRss, setHasCopiedRss] = useState(false);

  // Backup & Migration States
  const [backupConfig, setBackupConfig] = useState({
    settings: true,
    follows: true,
    posts: true,
    media: true
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [confirmRestore, setConfirmRestore] = useState<{ isOpen: boolean; file: File | null }>({
    isOpen: false,
    file: null
  });

  // FIX: Changed 'boolean' type to 'false' value for initial state
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; id: string; label: string }>({
    isOpen: false,
    id: '',
    label: ''
  });

  const sortedFollows = useMemo(() => {
    return [...follows].sort((a, b) => {
      if (a.isHome) return -1;
      if (b.isHome) return 1;
      return 0;
    });
  }, [follows]);

  useEffect(() => {
    const fetchSettings = async () => {
      const token = localStorage.getItem('yoursite-token');
      try {
        const res = await fetch('/api/settings', {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const settings = await res.json();
          setIsPublic(settings.is_public === 'true');
          setAvatarUrl(settings.avatar_url || null);
          setGlobalPollInterval(parseInt(settings.global_poll_interval || '60'));
        }
      } catch (e) {
        console.error("Failed to load settings");
      }
    };
    fetchSettings();
  }, []);

  const togglePublic = async () => {
    const newValue = !isPublic;
    updateSetting('is_public', String(newValue));
    setIsPublic(newValue);
  };

  const updateSetting = async (key: string, value: string) => {
    const token = localStorage.getItem('yoursite-token');
    try {
      await fetch('/api/settings', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ key, value })
      });
    } catch (e) {
      console.error("Failed to update setting", key);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = localStorage.getItem('yoursite-token');
    const formData = new FormData();
    formData.append('avatar', file);
    setIsUploadingAvatar(true);
    try {
      const res = await fetch('/api/settings/avatar', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setAvatarUrl(data.avatarUrl);
      }
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFilePreview(URL.createObjectURL(file));
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  const commitTag = (val: string) => {
    const cleaned = val.trim().replace(/,/g, '');
    if (cleaned && !tags.includes(cleaned)) {
      setTags([...tags, cleaned]);
      setTagInput('');
    } else {
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      commitTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  };

  const handlePostSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (activeLimit && newPostContent.length > activeLimit) return;
    if (!newPostContent.trim() && !selectedFile) return;
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('content', newPostContent);
    formData.append('type', newPostType);
    formData.append('isNsfw', String(isNsfw));
    const finalTags = [...tags];
    if (tagInput.trim()) {
      const cleaned = tagInput.trim().replace(/,/g, '');
      if (cleaned && !finalTags.includes(cleaned)) finalTags.push(cleaned);
    }
    formData.append('tags', JSON.stringify(finalTags));
    if (selectedFile) formData.append('media', selectedFile);

    const success = await onPostCreate(formData);
    if (success) {
      setNewPostContent('');
      setTags([]);
      setTagInput('');
      setIsNsfw(false);
      clearSelectedFile();
    }
    setIsSubmitting(false);
  };

  const handleThemeChange = (color: string) => {
    onThemeColorChange(color);
    updateSetting('theme_color', color);
  };

  const handleExport = async () => {
    setIsExporting(true);
    const token = localStorage.getItem('yoursite-token');
    const params = new URLSearchParams(backupConfig as any).toString();
    try {
      const res = await fetch(`/api/backup/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `yoursite-archive-${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Export failed", e);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      alert("Please upload a .zip archive generated by YourSite.");
      return;
    }
    setConfirmRestore({ isOpen: true, file });
    e.target.value = '';
  };

  const executeRestore = async () => {
    if (!confirmRestore.file) return;
    setIsImporting(true);
    setImportStatus('Uploading archive...');
    const token = localStorage.getItem('yoursite-token');
    const formData = new FormData();
    formData.append('archive', confirmRestore.file);

    try {
      setImportStatus('Reconstructing your world...');
      const res = await fetch('/api/backup/import', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        setImportStatus('Success! Site rebooting...');
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const err = await res.json();
        alert("Restore failed: " + err.error);
        setIsImporting(false);
      }
    } catch (e) {
      console.error("Restore failed", e);
      alert("Restore failed: Connection error.");
      setIsImporting(false);
    }
  };

  const isMediaType = newPostType === PostType.IMAGE || newPostType === PostType.VIDEO || newPostType === PostType.AUDIO;
  
  const activeLimit = useMemo(() => {
    if (newPostType === PostType.SHORT) return SHORT_POST_LIMIT;
    if (newPostType === PostType.LONG) return LONG_POST_LIMIT;
    return undefined;
  }, [newPostType]);

  const currentLength = newPostContent.length;
  const isOverLimit = activeLimit !== undefined && currentLength > activeLimit;
  const isNearLimit = activeLimit !== undefined && currentLength >= activeLimit * 0.9;

  const handleCopyRssUrl = () => {
    const url = `${window.location.origin}/api/feed/rss`;
    navigator.clipboard.writeText(url);
    setHasCopiedRss(true);
    setTimeout(() => setHasCopiedRss(false), 2000);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 relative">
      <ConfirmDialog 
        isOpen={confirmDelete.isOpen}
        title={`Unfollow ${confirmDelete.label}?`}
        message={`All posts and media associated with this feed will be permanently removed.`}
        confirmLabel="Unfollow"
        variant="danger"
        onClose={() => setConfirmDelete({ ...confirmDelete, isOpen: false })}
        onConfirm={() => onFollowDelete(confirmDelete.id)}
      />

      <ConfirmDialog 
        isOpen={confirmRestore.isOpen}
        title="Wipe & Restore Site?"
        message={`You are about to permanently replace your existing data with the contents of "${confirmRestore.file?.name}". This is destructive and irreversible.`}
        confirmLabel="Overwrite Everything"
        variant="danger"
        onClose={() => setConfirmRestore({ isOpen: false, file: null })}
        onConfirm={executeRestore}
      />

      {isAddFeedOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsAddFeedOpen(false)}></div>
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl relative border border-gray-100 dark:border-gray-800 transform transition-all animate-in zoom-in-95 duration-200">
            <h3 className="text-2xl font-bold mb-2">Follow New Source</h3>
            <p className="text-gray-500 text-sm mb-6">Enter an RSS, Atom, or JSON feed URL.</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setIsAddingFeed(true);
              const success = await onFollowAdd({ url: newFeedUrl, label: newFeedUrlLabel || new URL(newFeedUrl).hostname });
              if (success) { setNewFeedUrl(''); setNewFeedLabel(''); setIsAddFeedOpen(false); }
              setIsAddingFeed(false);
            }} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1 text-indigo-600">Feed URL</label>
                <input type="url" required value={newFeedUrl} onChange={(e) => setNewFeedUrl(e.target.value)} placeholder="https://example.com/feed.xml" className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Label (Optional)</label>
                <input type="text" value={newFeedUrlLabel} onChange={(e) => setNewFeedLabel(e.target.value)} placeholder="My Favorite Source" className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setIsAddFeedOpen(false)} className="flex-1 py-4 bg-gray-100 dark:bg-slate-800 text-gray-600 rounded-2xl font-bold">Cancel</button>
                <button type="submit" disabled={isAddingFeed || !newFeedUrl.trim()} className="flex-[2] py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg">{isAddingFeed ? <i className="fa-solid fa-spinner animate-spin mr-2"></i> : null} Subscribe</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="md:col-span-3 space-y-4">
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm">
          <h2 className="font-bold text-lg mb-6 flex items-center gap-2"><i className="fa-solid fa-gauge-high text-indigo-600"></i> Workspace</h2>
          <nav className="space-y-1">
            <button onClick={() => setActiveTab('composer')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all ${activeTab === 'composer' ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20' : 'text-gray-500'}`}><i className="fa-solid fa-pen-to-square"></i> Create Post</button>
            <button onClick={() => setActiveTab('subscriptions')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all ${activeTab === 'subscriptions' ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20' : 'text-gray-500'}`}><i className="fa-solid fa-rss"></i> Subscriptions</button>
            <button onClick={() => setActiveTab('system')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all ${activeTab === 'system' ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20' : 'text-gray-500'}`}><i className="fa-solid fa-server"></i> System</button>
            <div className="pt-4 border-t border-gray-100 dark:border-gray-800 mt-4">
              <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-rose-500 hover:bg-rose-50 transition-all"><i className="fa-solid fa-power-off"></i> Logout</button>
            </div>
          </nav>
        </div>
        <div className="bg-indigo-600 rounded-3xl p-6 text-white shadow-lg">
          <h3 className="text-xs font-bold uppercase opacity-60 mb-4">Quick Stats</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><div className="text-2xl font-bold">{stats.postCount}</div><div className="text-[10px] opacity-80">Local Posts</div></div>
            <div><div className="text-2xl font-bold">{stats.followCount}</div><div className="text-[10px] opacity-80">Follows</div></div>
          </div>
        </div>
      </div>

      <div className="md:col-span-9">
        {activeTab === 'composer' && (
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">New Post</h2>
            <form onSubmit={handlePostSubmit} className="space-y-6">
              <div className="flex flex-wrap gap-2 p-1 bg-gray-50 dark:bg-slate-800 rounded-2xl w-fit">
                {(['short', 'long', 'image', 'video', 'audio'] as PostType[]).map(t => (
                  <button key={t} type="button" onClick={() => { setNewPostType(t); clearSelectedFile(); }} className={`px-4 py-2 rounded-xl text-xs font-bold capitalize transition-all ${newPostType === t ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600' : 'text-gray-400'}`}>{t}</button>
                ))}
              </div>

              {isMediaType && (
                <div className="space-y-4">
                  <div onClick={() => mediaInputRef.current?.click()} className={`group relative w-full h-48 sm:h-64 rounded-3xl border-2 border-dashed transition-all flex flex-col items-center justify-center gap-3 cursor-pointer overflow-hidden ${selectedFile ? 'border-indigo-500 bg-indigo-50/10' : 'border-gray-200 dark:border-slate-800 hover:border-indigo-400 hover:bg-indigo-50/5'}`}>
                    {filePreview ? (
                      <>
                        {newPostType === PostType.IMAGE && <img src={filePreview} className="w-full h-full object-cover" alt="Preview" />}
                        {newPostType === PostType.VIDEO && <video src={filePreview} className="w-full h-full object-cover" />}
                        {newPostType === PostType.AUDIO && <div className="flex flex-col items-center gap-2"><i className="fa-solid fa-file-audio text-5xl text-indigo-500"></i><span className="text-sm font-medium text-gray-500 truncate max-w-[200px]">{selectedFile?.name}</span></div>}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><span className="text-white font-bold bg-indigo-600 px-4 py-2 rounded-full">Change File</span></div>
                      </>
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center text-indigo-600"><i className={`fa-solid ${newPostType === PostType.IMAGE ? 'fa-image' : newPostType === PostType.VIDEO ? 'fa-video' : 'fa-microphone-lines'} text-2xl`}></i></div>
                        <div className="text-center"><p className="font-bold text-gray-700 dark:text-gray-200">Select {newPostType}</p><p className="text-xs text-gray-400">Drag and drop or click to browse</p></div>
                      </>
                    )}
                  </div>
                  <input type="file" ref={mediaInputRef} className="hidden" onChange={handleFileChange} accept={newPostType === PostType.IMAGE ? 'image/*' : newPostType === PostType.VIDEO ? 'video/*' : 'audio/*'} />
                  <div className="flex items-center justify-between gap-4">
                    {selectedFile && <button type="button" onClick={clearSelectedFile} className="text-xs font-bold text-rose-500 hover:underline flex items-center gap-1"><i className="fa-solid fa-circle-xmark"></i> Remove file</button>}
                    <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-800 px-4 py-2 rounded-xl border border-gray-100 dark:border-gray-700 ml-auto">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-tight">NSFW Content</span>
                      <button type="button" onClick={() => setIsNsfw(!isNsfw)} className={`w-12 h-6 rounded-full transition-colors relative shadow-inner ${isNsfw ? 'bg-rose-500' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-md ${isNsfw ? 'left-7' : 'left-1'}`}></div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="relative group/textarea">
                <textarea 
                  value={newPostContent} 
                  onChange={(e) => setNewPostContent(e.target.value)} 
                  placeholder={isMediaType ? `Add a caption...` : "What's on your mind?"} 
                  rows={newPostType === PostType.LONG ? 12 : 3} 
                  maxLength={activeLimit}
                  className={`w-full p-6 bg-gray-50 dark:bg-slate-800 rounded-3xl focus:ring-2 outline-none text-lg leading-relaxed transition-all ${isOverLimit ? 'ring-2 ring-rose-500 border-rose-500' : 'focus:ring-indigo-500'}`} 
                />
                
                {activeLimit !== undefined && (
                  <div className={`absolute bottom-4 right-6 flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black tracking-tighter transition-all duration-300 ${
                    currentLength >= activeLimit 
                      ? 'bg-rose-500 text-white animate-bounce' 
                      : isNearLimit 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-indigo-50/50 dark:bg-slate-700/50 text-indigo-400 dark:text-gray-500'
                  }`}>
                    {currentLength} / {activeLimit}
                    {currentLength >= activeLimit && <i className="fa-solid fa-circle-exclamation text-[8px]"></i>}
                  </div>
                )}
              </div>

              {isOverLimit && (
                <div className="p-4 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 rounded-2xl text-xs font-bold flex items-center gap-2 border border-rose-100 dark:border-rose-800 animate-in slide-in-from-top-1">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  <span>Post content exceeds the {activeLimit} character limit for {newPostType} posts. Please trim it down.</span>
                </div>
              )}

              <div className="space-y-3">
                <label className="text-xs font-bold text-gray-400 uppercase ml-1 tracking-wider">Tags</label>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 dark:bg-slate-800 rounded-2xl border-2 border-transparent focus-within:border-indigo-500 transition-all shadow-inner">
                  {tags.map(tag => (
                    <span key={tag} className="flex items-center gap-1 px-3 py-1 bg-indigo-600 text-white text-xs font-bold rounded-full shadow-sm animate-in zoom-in-75 duration-200">
                      #{tag}<button type="button" onClick={() => setTags(tags.filter(t => t !== tag))} className="hover:text-indigo-200 transition-colors ml-1"><i className="fa-solid fa-xmark text-[10px]"></i></button>
                    </span>
                  ))}
                  <input type="text" value={tagInput} onChange={(e) => e.target.value.endsWith(',') ? commitTag(e.target.value) : setTagInput(e.target.value)} onKeyDown={handleTagKeyDown} className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-1" placeholder={tags.length === 0 ? "Add tags..." : ""} />
                </div>
              </div>
              <div className="pt-4 flex justify-end">
                <button 
                  type="submit" 
                  disabled={isSubmitting || isOverLimit || (!newPostContent.trim() && !selectedFile)} 
                  className="px-8 py-3 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg disabled:opacity-50 transition-all hover:scale-[1.02] active:scale-95"
                >
                  {isSubmitting ? <i className="fa-solid fa-spinner animate-spin mr-2"></i> : null} Post to YourSite
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Following</h2>
                <button onClick={() => setIsAddFeedOpen(true)} className="px-6 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-indigo-700 shadow-md"><i className="fa-solid fa-plus"></i> Add Feed</button>
              </div>
              <div className="space-y-4">
                {sortedFollows.map(f => {
                  const hasEngagement = (f.engagementMultiplier || 0) > 0;
                  const isHome = !!f.isHome;
                  const isHealthy = (f.errorCount || 0) === 0;
                  const lastPolled = f.lastPolledAt ? new Date(f.lastPolledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Never';
                  const processedCount = f.lastProcessedCount ?? 0;
                  
                  return (
                    <div key={f.id} className={`p-6 rounded-2xl border transition-all flex flex-col gap-4 ${isHome ? 'bg-indigo-50/30 dark:bg-indigo-900/10 border-indigo-200 dark:border-indigo-800 shadow-inner' : 'bg-gray-50 dark:bg-slate-800 border-gray-100 dark:border-gray-700'}`}>
                      <div className="flex flex-col sm:flex-row gap-8">
                        {/* Left: Content & Interaction */}
                        <div className="flex-1 space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-bold text-lg ${isHome ? 'gradient-text' : ''}`}>{f.label}</h4>
                              {isHome && <span className="px-2 py-0.5 bg-indigo-600 text-white text-[9px] font-black rounded-md uppercase tracking-widest shadow-sm">Sovereign Source</span>}
                            </div>
                            <p className="text-xs text-gray-400 truncate">{isHome ? 'Internal database & local storage' : f.url}</p>
                          </div>
                          
                          {!isHome && f.lastError && (
                            <div className="p-3 bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-800 text-rose-600 dark:text-rose-400 rounded-xl text-[10px] font-medium animate-in fade-in slide-in-from-top-1">
                              <i className="fa-solid fa-circle-exclamation mr-2"></i> {f.lastError}
                            </div>
                          )}

                          <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                            <div className="flex-1 w-full sm:max-w-[200px]">
                              <div className="flex items-center justify-between mb-2">
                                <label className="block text-[9px] font-black text-gray-400 uppercase tracking-widest">Priority: {isHome ? '200' : f.weight}%</label>
                              </div>
                              <input type="range" min="0" max="200" value={isHome ? 200 : f.weight} disabled={isHome} onChange={(e) => !isHome && onFollowUpdate({...f, weight: parseInt(e.target.value)})} className={`w-full h-1.5 rounded-lg appearance-none transition-opacity ${isHome ? 'bg-indigo-400 cursor-not-allowed opacity-40' : 'bg-gray-200 dark:bg-slate-700 accent-indigo-600 cursor-pointer'}`} />
                            </div>
                            
                            <div className="flex items-center gap-1.5">
                              <button disabled={isHome} onClick={() => !isHome && onFollowUpdate({...f, muted: !f.muted})} title="Mute Feed" className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${isHome ? 'bg-indigo-100/50 text-indigo-200' : (f.muted ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 hover:text-indigo-600')}`}><i className={`fa-solid ${f.muted ? 'fa-volume-xmark' : 'fa-volume-high'}`}></i></button>
                              <button disabled={isHome} onClick={() => !isHome && onFollowUpdate({...f, blocked: !f.blocked})} title="Block Feed" className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${isHome ? 'bg-indigo-100/50 text-indigo-200' : (f.blocked ? 'bg-rose-100 text-rose-600' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 hover:text-rose-500')}`}><i className="fa-solid fa-ban"></i></button>
                              <button disabled={isHome} onClick={() => !isHome && setConfirmDelete({ isOpen: true, id: f.id, label: f.label })} title="Unfollow" className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${isHome ? 'bg-indigo-100/50 text-indigo-200' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 hover:bg-rose-500 hover:text-white'}`}><i className="fa-solid fa-trash-can"></i></button>
                              
                              {!isHome && (
                                <div className={`ml-2 flex items-center gap-1.5 px-2 py-1 rounded-lg text-[9px] font-bold transition-all ${hasEngagement ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-500' : 'bg-gray-100 dark:bg-slate-700/50 text-gray-400'}`}>
                                  <i className={`fa-solid ${hasEngagement ? 'fa-heart-pulse' : 'fa-heart'}`}></i>
                                  <span>+{ (f.engagementMultiplier || 0).toFixed(1) }x</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {!isHome && (
                          <div className="flex flex-col gap-1 min-w-[150px] sm:border-l border-gray-100 dark:border-gray-800 sm:pl-8 justify-center">
                            <div className={`flex items-center justify-between gap-x-3 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${isHealthy ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' : 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400'}`}>
                              <span>{isHealthy ? 'Healthy' : 'Sync Error'}</span>
                              <i className="fa-solid fa-circle text-[6px]"></i>
                            </div>
                            <div className="flex items-center justify-between gap-x-3 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-gray-100 dark:bg-slate-800/50 text-gray-500">
                              <span>Synced {lastPolled}</span>
                              <i className="fa-solid fa-clock-rotate-left"></i>
                            </div>
                            <div className={`flex items-center justify-between gap-x-3 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${processedCount > 0 ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600' : 'bg-gray-100 dark:bg-slate-800/50 text-gray-400'}`}>
                              <span>+{processedCount} Posts</span>
                              <i className="fa-solid fa-cloud-arrow-down"></i>
                            </div>
                          </div>
                        )}
                      </div>

                      {isHome && (
                        <div className="mt-4 p-5 bg-white/50 dark:bg-slate-900/50 rounded-2xl border border-indigo-100 dark:border-indigo-800/50 animate-in fade-in slide-in-from-top-2 duration-500">
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-1.5">
                              <i className="fa-solid fa-share-nodes text-[8px]"></i> Public RSS Feed URL
                            </label>
                            <span className="text-[9px] font-bold text-gray-400 italic">Share this with friends or feed readers</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-50 dark:bg-slate-800/80 px-4 py-2.5 rounded-xl text-xs font-mono text-gray-500 dark:text-gray-400 truncate border border-gray-100 dark:border-gray-700/50">
                              {window.location.origin}/api/feed/rss
                            </div>
                            <button 
                              onClick={handleCopyRssUrl}
                              className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 min-w-[100px] justify-center shadow-sm ${
                                hasCopiedRss 
                                  ? 'bg-emerald-500 text-white' 
                                  : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95'
                              }`}
                            >
                              <i className={`fa-solid ${hasCopiedRss ? 'fa-check' : 'fa-copy'} transition-transform duration-300`}></i>
                              {hasCopiedRss ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="space-y-6 pb-12">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 shadow-sm">
              <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><i className="fa-solid fa-id-card text-indigo-600"></i> Site Identity</h3>
              <div className="flex flex-col sm:flex-row items-center gap-8">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-[2.5rem] bg-indigo-50 dark:bg-slate-800 border-2 border-dashed border-indigo-200 dark:border-slate-700 overflow-hidden flex items-center justify-center relative">
                    {avatarUrl ? <img src={avatarUrl} className="w-full h-full object-cover" alt="Avatar" /> : <i className="fa-solid fa-user text-4xl text-indigo-200"></i>}
                    {isUploadingAvatar && <div className="absolute inset-0 bg-white/60 dark:bg-slate-900/60 flex items-center justify-center"><i className="fa-solid fa-spinner animate-spin text-indigo-600 text-2xl"></i></div>}
                  </div>
                  <button onClick={() => avatarInputRef.current?.click()} className="absolute -bottom-2 -right-2 w-10 h-10 bg-indigo-600 text-white rounded-2xl shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-all"><i className="fa-solid fa-camera text-sm"></i></button>
                  <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={handleAvatarUpload} />
                </div>
                <div className="flex-1 space-y-2 text-center sm:text-left"><h4 className="font-bold text-xl">Profile Picture</h4><p className="text-gray-500 text-sm">Square images work best. Max 2MB.</p></div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
                <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><i className="fa-solid fa-palette text-indigo-600"></i> Appearance</h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-5 gap-3">
                  {Object.keys(THEME_COLORS).map((colorName) => (
                    <button key={colorName} onClick={() => handleThemeChange(colorName)} className={`w-10 h-10 rounded-xl border-2 transition-all hover:scale-110 ${themeColor === colorName ? 'border-indigo-600 shadow-lg' : 'border-transparent shadow-sm'}`} style={{ backgroundColor: THEME_COLORS[colorName].primary }} />
                  ))}
                </div>
              </div>
              
              <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col justify-between">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><i className="fa-solid fa-eye text-indigo-600"></i> Visibility</h3>
                <div className="flex-1 flex flex-col justify-center">
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                    <div><div className="font-bold text-sm">Public Site</div><div className="text-[10px] text-gray-500 uppercase font-black tracking-tighter">Allow guests and RSS users</div></div>
                    <button onClick={togglePublic} className={`w-12 h-6 rounded-full transition-colors relative shadow-inner ${isPublic ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-md ${isPublic ? 'left-7' : 'left-1'}`}></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-5">
                <i className="fa-solid fa-cloud-arrow-up text-8xl"></i>
              </div>
              <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><i className="fa-solid fa-box-archive text-indigo-600"></i> Backup & Migration</h3>
              <p className="text-gray-500 text-sm mb-8">Export everything into a compressed .zip archive. This includes database records and raw media files.</p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                <div className="flex flex-col space-y-6">
                  <div className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Export Contents</div>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(backupConfig).map(([key, value]) => (
                      <button 
                        key={key} 
                        onClick={() => setBackupConfig({...backupConfig, [key]: !value})}
                        className={`p-4 rounded-2xl border text-left flex items-center gap-3 transition-all ${value ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-gray-50 border-gray-100 text-gray-400'}`}
                      >
                        <i className={`fa-solid ${value ? 'fa-check-circle' : 'fa-circle'} text-sm`}></i>
                        <span className="text-sm font-bold capitalize">{key}</span>
                      </button>
                    ))}
                  </div>
                  <button 
                    onClick={handleExport}
                    disabled={isExporting || !Object.values(backupConfig).some(Boolean)}
                    className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-sm shadow-sm border border-white/10 flex items-center justify-center gap-2 hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {isExporting ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-download"></i>}
                    Generate Site Archive (.zip)
                  </button>
                </div>

                <div className="flex flex-col space-y-6">
                  <div className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Restore Data</div>
                  <div className="relative group flex-grow p-0.5 overflow-hidden">
                    <input 
                      type="file" 
                      accept=".zip" 
                      onChange={handleImportFileSelect}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-not-allowed"
                      disabled={isImporting}
                    />
                    <div className={`h-full min-h-[160px] p-8 rounded-[2rem] border-2 border-dashed flex flex-col items-center justify-center text-center transition-all ${isImporting ? 'bg-indigo-50 border-indigo-300' : 'border-gray-200 dark:border-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-slate-800 group-hover:border-indigo-400'}`}>
                      {isImporting ? (
                        <div className="animate-pulse flex flex-col items-center gap-4">
                          <i className="fa-solid fa-server text-indigo-600 text-3xl"></i>
                          <div className="text-sm font-bold text-indigo-600">{importStatus}</div>
                        </div>
                      ) : (
                        <>
                          <i className="fa-solid fa-file-import text-gray-300 text-4xl mb-4 group-hover:text-indigo-400 group-hover:scale-110 transition-all"></i>
                          <p className="text-sm font-bold text-gray-600 dark:text-gray-300 mb-1">Restore from Archive</p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Click or drag .zip file here</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminView;
